import { makeStyles, Text, tokens } from '@fluentui/react-components';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { postOAuth2ResultToOpener } from './oauth2PopupPostMessage';
import { parseOAuth2CallbackUrl } from './parseOAuth2CallbackUrl';

type OAuth2PopupCallbackStatus = 'pending' | 'posted' | 'no-opener';

const useStyles = makeStyles({
    root: {
        minHeight: '100vh',
        padding: tokens.spacingHorizontalXXL,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        gap: tokens.spacingVerticalM,
        backgroundColor: tokens.colorNeutralBackground1,
        color: tokens.colorNeutralForeground1,
        textAlign: 'center',
    },
});

/**
 * OAuth2 redirect target for a login popup opened via window.open from the parent app.
 * Reads bearer token (or error) from the callback URL, posts it to window.opener via postMessage,
 * then attempts to close the popup.
 */
const OAuth2PopupCallback: React.FC = () => {
    const styles = useStyles();
    const { t } = useTranslation();
    const [status, setStatus] = useState<OAuth2PopupCallbackStatus>('pending');

    useEffect(() => {
        const opener = window.opener as Window | null;
        if (!opener) {
            setStatus('no-opener');
            return;
        }

        const parsed = parseOAuth2CallbackUrl();
        const targetOrigin = window.location.origin;
        postOAuth2ResultToOpener(opener, parsed, targetOrigin);
        setStatus('posted');

        const close = (): void => {
            window.close();
        };

        const timer = window.setTimeout(close, 100);
        return () => window.clearTimeout(timer);
    }, []);

    const message =
        status === 'no-opener'
            ? t('oauth2PopupCallbackNoOpener')
            : status === 'posted'
            ? t('oauth2PopupCallbackComplete')
            : t('oauth2PopupCallbackProcessing');

    return (
        <div className={styles.root}>
            <Text>{message}</Text>
        </div>
    );
};

export default OAuth2PopupCallback;
